import { FC, useState, Fragment } from "react";
import {
    Flex,
    Heading,
    Text,
    Grid,
    GridItem,
    useBreakpointValue,
    HorizontalScroll,
    Box,
    Icon,
    Button,
    Spacer,
} from "@smile2impress/components/relaunch";
import {
    ArrowLeftCircledIcon,
    ArrowRightCircledIcon,
} from "@smile2impress/components";
import { useGoToBooking } from "hooks/useGoToBooking";

import { Swiper } from "components/Relaunch/Swiper";
import Link from "components/Link";

import {
    MultiColumnSliderProps,
    DirectionType,
} from "./MultiColumnSlider.types";
import { ColumnBeforeAndAfter } from "./components";

export const MultiColumnSlider: FC<MultiColumnSliderProps> = ({
    content,
    headingAs,
    itemComponent,
}) => {
    const {
        title,
        subtitle,
        items,
        cta,
        withExpansion = false,
        mobileSwiper = false,
        disclaimer,
        itemContent,
        direction = DirectionType.Vertical,
        gridSpacing,
    } = content;
    const { goToBooking } = useGoToBooking();
    if (cta && !cta.onClick && !cta.link) {
        cta.onClick = goToBooking;
    }

    const [sliderPosition, setSliderPosition] = useState(0);

    const pageSize = 3;
    // Width of the content - 6 columns (6fr)
    // Width of the gap - 2 columns (2fr)
    const elementWidth = 6;
    const gapWidth = 2;
    const columnWidth =
        100 / (pageSize * elementWidth + (pageSize - 1) * gapWidth);
    const columnWithGapWidth = columnWidth * (elementWidth + gapWidth);
    const gridSizing = gridSpacing
        ? `repeat(${items.length}, 1fr)`
        : new Array(items.length)
              .fill(`${elementWidth}fr`)
              .join(` ${gapWidth}fr `);
    // Extend container width based on total amount of elements
    const extraItems = items.length - pageSize;
    const totalWidth = gridSpacing
        ? extraItems > 0
            ? `calc(${
                  (items.length / pageSize) * 100
              }% + ${extraItems} * ${gridSpacing} / ${pageSize})`
            : "100%"
        : `${100 + columnWithGapWidth * extraItems}%`;
    // Each slide moves by 1 card width + 1 gap = (containerWidth + gap) / pageSize
    const slideStep = gridSpacing
        ? (pos: number) =>
              `calc(-${
                  (pos * 100) / pageSize
              }% - ${pos} * ${gridSpacing} / ${pageSize})`
        : null;

    const isMobile = useBreakpointValue({
        base: true,
        tablet: true,
        desktop: false,
        desktopXL: false,
    });

    const Component = itemComponent || ColumnBeforeAndAfter;
    type ComponentProps = React.ComponentProps<typeof Component>;

    const slideLeft = () => {
        setSliderPosition((prev) => {
            if (prev !== 0) return prev - 1;
            else return items.length - pageSize;
        });
    };

    const slideRight = () => {
        setSliderPosition((prev) => {
            if (prev !== items.length - pageSize) return prev + 1;
            return 0;
        });
    };

    return (
        <Flex direction="column" px={[0, 0, 4]}>
            <Flex direction="row" mb={[3, 3, 4]} justifyContent="space-between">
                <Box maxW={["100%", "100%", "60%"]}>
                    <Heading
                        maxW="800px"
                        textStyle={["title1"]}
                        color={["text.base", "text.base", "text.base"]}
                        as={headingAs}
                    >
                        {title}
                    </Heading>
                    {!!subtitle && (
                        <Text textStyle="body" mt={[1]}>
                            {subtitle}
                        </Text>
                    )}
                </Box>
                {!isMobile && (
                    <Flex alignItems="flex-end">
                        {items.length > pageSize && (
                            <Button
                                variant="ghost"
                                px={0}
                                py={0}
                                mr={0.7}
                                onClick={slideLeft}
                            >
                                <Icon
                                    w={3}
                                    h={3}
                                    as={ArrowLeftCircledIcon}
                                    color="base.gunmetal"
                                />
                            </Button>
                        )}

                        {cta && (
                            <Button
                                variant={cta.variant || "stroked"}
                                {...(cta.onClick
                                    ? { onClick: cta.onClick }
                                    : {})}
                                {...(cta.link
                                    ? { as: Link, href: cta.link }
                                    : {})}
                                mr={[0, 0, 0.7]}
                            >
                                {cta.text}
                            </Button>
                        )}
                        {items.length > pageSize && (
                            <Button
                                variant="ghost"
                                px={0}
                                py={0}
                                onClick={slideRight}
                            >
                                <Icon
                                    w={3}
                                    h={3}
                                    as={ArrowRightCircledIcon}
                                    color="base.gunmetal"
                                />
                            </Button>
                        )}
                    </Flex>
                )}
            </Flex>

            {isMobile ? (
                <>
                    <Flex direction="row" mx={[-2]}>
                        {mobileSwiper ? (
                            <Box>
                                <Swiper
                                    items={items}
                                    withExpansion={withExpansion}
                                    itemComponent={Component}
                                    itemContent={itemContent}
                                    direction={direction}
                                />
                            </Box>
                        ) : (
                            <HorizontalScroll scrollElementProps={{ mb: 0 }}>
                                <Flex gap={[2]} alignItems="flex-start" pl={2}>
                                    {items.map((item, index) => (
                                        <Flex
                                            minWidth={8}
                                            key={index}
                                            mb={[-2]}
                                            alignSelf="stretch"
                                        >
                                            <Component
                                                {...(item as ComponentProps)}
                                                {...itemContent}
                                                withExpansion={withExpansion}
                                                direction={direction}
                                            />
                                        </Flex>
                                    ))}
                                    {/* hack to create a gap on the right */}
                                    <Spacer minW="0.1px" />
                                </Flex>
                            </HorizontalScroll>
                        )}
                    </Flex>
                    {disclaimer && (
                        <Box
                            mt={[1, 1, 3]}
                            textAlign={["center", "center", "left"]}
                        >
                            <Text
                                textStyle={["captionSmall"]}
                                color="base.halfBlack"
                            >
                                {disclaimer}
                            </Text>
                        </Box>
                    )}
                    {cta && (
                        <Button
                            variant={cta.variant || "stroked"}
                            {...(cta.onClick ? { onClick: cta.onClick } : {})}
                            {...(cta.link ? { as: Link, href: cta.link } : {})}
                            mt={2}
                            mr={0.5}
                        >
                            {cta.text}
                        </Button>
                    )}
                </>
            ) : (
                <>
                    <Box overflowX={"hidden"}>
                        <Grid
                            gridTemplateColumns={gridSizing}
                            {...(gridSpacing && { gridColumnGap: gridSpacing })}
                            width={totalWidth}
                            marginLeft={
                                gridSpacing && slideStep
                                    ? sliderPosition > 0
                                        ? slideStep(sliderPosition)
                                        : "0"
                                    : `${
                                          sliderPosition *
                                          -1 *
                                          columnWithGapWidth
                                      }%`
                            }
                            transition={"margin-left 0.3s ease-in-out"}
                        >
                            {items.map((item, index) => (
                                <Fragment key={index}>
                                    <GridItem>
                                        <Component
                                            {...(item as ComponentProps)}
                                            {...itemContent}
                                            withExpansion={withExpansion}
                                            direction={direction}
                                        />
                                    </GridItem>
                                    {!gridSpacing &&
                                        items.length - index >= 2 && (
                                            <GridItem />
                                        )}
                                </Fragment>
                            ))}
                        </Grid>
                    </Box>
                    {disclaimer && (
                        <Box mt={3}>
                            <Text
                                textStyle={["captionSmall"]}
                                color="base.halfBlack"
                            >
                                {disclaimer}
                            </Text>
                        </Box>
                    )}
                </>
            )}
        </Flex>
    );
};
